import { useState } from "react";
import { useParams } from "react-router";
import { useFormatter, useLocale, useTranslations } from "use-intl";
import { Medal, Pencil } from "lucide-react";
import { lt } from "@liftquest/shared/localized";
import { Page, PageHeader, Section } from "@/components/app/page-header";
import { NotFound, PageLoading } from "@/components/app/states";
import { Card } from "@/components/ui/card";
import { useSessionDetail } from "@/lib/queries";
import { DeleteSessionButton } from "./delete-session-button";
import { EditSetDrawer, type EditableSet } from "./edit-set-drawer";

const FEELINGS = ["", "😫", "😕", "🙂", "😃", "🤩"];

type Group = {
  name: string;
  type: "reps" | "time" | "cardio";
  weightStep: number;
  sets: NonNullable<ReturnType<typeof useSessionDetail>["data"]>["sets"];
};

export function SessionPage() {
  const { sessionId = "" } = useParams();
  const locale = useLocale();
  const t = useTranslations("progress");
  const tf = useTranslations("finish");
  const format = useFormatter();
  const detail = useSessionDetail(sessionId);
  const [editing, setEditing] = useState<{ set: EditableSet; group: Group } | null>(null);
  if (detail.isPending) return <PageLoading />;
  if (!detail.data) return <NotFound />;
  const { sets } = detail.data;
  const session = {
    ...detail.data.session,
    startedAt: new Date(detail.data.session.startedAt),
    finishedAt: detail.data.session.finishedAt ? new Date(detail.data.session.finishedAt) : null,
  };

  const groups = new Map<string, Group>();
  for (const row of sets) {
    const g = groups.get(row.exercise.id) ?? {
      name: lt(row.exercise.name, locale),
      type: row.exercise.type,
      weightStep: row.exercise.weightStep,
      sets: [],
    };
    g.sets.push(row);
    groups.set(row.exercise.id, g);
  }
  const minutes = session.finishedAt
    ? Math.max(1, Math.round((session.finishedAt.getTime() - session.startedAt.getTime()) / 60000))
    : null;

  return (
    <Page>
      <PageHeader
        backHref="/progress"
        title={lt(session.workoutName, locale)}
        subtitle={
          <>
            {format.dateTime(session.startedAt, { dateStyle: "full", timeStyle: "short" })}
            {session.loggedLater && ` · ${t("loggedLater")}`}
          </>
        }
      />
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Card className="gap-0 py-3">
          <p className="text-lg font-bold">{minutes ?? "–"} min</p>
          <p className="text-xs text-muted-foreground">{tf("duration")}</p>
        </Card>
        <Card className="gap-0 py-3">
          <p className="text-lg font-bold text-xp-foreground dark:text-xp">+{session.xp}</p>
          <p className="text-xs text-muted-foreground">XP</p>
        </Card>
        <Card className="gap-0 py-3">
          <p className="text-lg font-bold">{session.feeling ? FEELINGS[session.feeling] : "–"}</p>
          <p className="text-xs text-muted-foreground">{t("feeling")}</p>
        </Card>
      </div>
      {session.notes && (
        <Section title={t("notes")}>
          <p className="rounded-2xl bg-muted/60 p-3 text-sm">{session.notes}</p>
        </Section>
      )}
      {session.finishedAt && sets.length > 0 && <p className="mt-4 text-xs text-muted-foreground">{t("tapToEdit")}</p>}
      {[...groups.values()].map((g) => (
        <Section key={g.name} title={g.name}>
          <ul className="divide-y rounded-2xl border bg-card">
            {g.sets.map(({ set }, i) => (
              <li key={set.id}>
                <button
                  type="button"
                  disabled={!session.finishedAt}
                  onClick={() => setEditing({ set, group: g })}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm enabled:hover:bg-muted/50"
                >
                  <span className="w-16 text-muted-foreground">{set.isWarmup ? t("warmup") : `#${i + 1}`}</span>
                  <span className="flex-1 font-medium tabular-nums">
                    {g.type === "reps"
                      ? `${set.weight ? format.number(set.weight) + " kg × " : ""}${set.reps}`
                      : g.type === "time"
                        ? `${set.durationSec} s`
                        : `${Math.round((set.durationSec ?? 0) / 60)} min${set.distanceKm ? ` · ${format.number(set.distanceKm)} km` : ""}`}
                  </span>
                  {set.rir != null && <span className="text-xs text-muted-foreground">{t("rir", { rir: set.rir })}</span>}
                  {set.isPr && <Medal className="size-4 text-xp" />}
                  {session.finishedAt && <Pencil className="size-3.5 text-muted-foreground" />}
                </button>
              </li>
            ))}
          </ul>
        </Section>
      ))}
      <EditSetDrawer
        set={editing?.set ?? null}
        title={editing?.group.name ?? ""}
        type={editing?.group.type ?? "reps"}
        weightStep={editing?.group.weightStep ?? 2.5}
        onClose={() => setEditing(null)}
      />
      <DeleteSessionButton sessionId={session.id} />
    </Page>
  );
}
